import type { WorkflowView } from '@/lib/api/generated/model';

import { parseTransitionEdgeId, transitionEdgeId } from '../builder/selection';
import { DONE_NODE_ID } from '../builder/types';
import type { WorkflowDetailSearch } from '../searchParams';

/**
 * What the read-only graph's panel is about.
 *
 * A union rather than two independent values, for the reason the builder's own
 * selection is one: a step and a transition are alternatives, and modelling
 * them as two optional fields makes "both at once" representable — a state no
 * code would then be allowed to assume away.
 *
 * The three kinds mirror the builder's (`builder/selection.ts`) deliberately.
 * Each of them was a thing you could select while editing a workflow and not
 * while reading one, which made the same object behave differently depending on
 * which mode you happened to be in.
 */
export type GraphSelection =
  | { kind: 'step'; id: string }
  | { kind: 'transition'; stepId: string; key: string }
  | { kind: 'phase'; id: string }
  | null;

/**
 * Resolve the address into a selection, dropping anything that names nothing.
 *
 * Both parameters are user-editable text and both outlive what they point at —
 * a link shared before a step was renamed still says `?step=specify`. Every
 * failure degrades to "nothing selected" rather than failing the route, which
 * is the same contract `searchParams.ts` documents for each parameter alone.
 *
 * `edge` wins when both are somehow present. Navigation only ever sets one, so
 * a URL carrying both was typed or truncated by hand, and the more specific of
 * the two is the better guess at what was meant.
 */
export function resolveGraphSelection(
  workflow: WorkflowView,
  search: WorkflowDetailSearch,
): GraphSelection {
  if (search.edge !== undefined) {
    const { stepId, key } = parseTransitionEdgeId(search.edge);
    const owner = findStep(workflow, stepId)?.step;
    if (key && owner?.transitions?.some((t) => t.key === key)) {
      return { kind: 'transition', stepId, key };
    }
  }

  if (search.step !== undefined && knownStep(workflow, search.step)) {
    return { kind: 'step', id: search.step };
  }

  if (
    search.phase !== undefined &&
    workflow.phases.some((phase) => phase.id === search.phase)
  ) {
    return { kind: 'phase', id: search.phase };
  }

  return null;
}

/**
 * The step with this id, and the phase that owns it.
 *
 * Exported because the panel needs the same lookup, and two copies of "walk the
 * phases looking for a step" is one copy too many for something this easy to
 * write slightly differently.
 */
export function findStep(workflow: WorkflowView, stepId: string) {
  for (const phase of workflow.phases) {
    for (const step of phase.steps ?? []) {
      if (step.id === stepId) return { step, phase };
    }
  }
  return undefined;
}

/** `$done` in the document is the done step on the canvas. */
export function resolveTransitionTarget(
  workflow: WorkflowView,
  to: string,
): string {
  return to === DONE_NODE_ID ? workflow.done_step.id : to;
}

/**
 * The system steps count as known.
 *
 * Clicking Backlog or Done opens a panel saying what it is, rather than doing
 * nothing and reading as broken (FR-046) — so an address naming one is a real
 * selection, even though neither is a declared step.
 */
function knownStep(workflow: WorkflowView, stepId: string): boolean {
  if (stepId === workflow.start_step.id || stepId === workflow.done_step.id) {
    return true;
  }
  return findStep(workflow, stepId) !== undefined;
}

/**
 * The address for a selection: one parameter naming what the panel is about.
 *
 * The inverse of {@link resolveGraphSelection}, and here rather than in the
 * route for the reason the router is an edge (constitution 2.1): which
 * parameter names which kind is a rule about this feature's view state, not
 * about routing, and the route should not have to know the id grammar a
 * transition is addressed by.
 */
export function graphSelectionSearch(
  selection: NonNullable<GraphSelection>,
): WorkflowDetailSearch {
  switch (selection.kind) {
    case 'step':
      return { step: selection.id };
    case 'transition':
      return { edge: transitionEdgeId(selection.stepId, selection.key) };
    case 'phase':
      return { phase: selection.id };
  }
}

/**
 * A stable key for a selection, for remounting the panel when the subject
 * changes.
 *
 * Distinct from {@link graphSelectionSearch} on purpose: that one is an
 * address, and two kinds sharing a bare id would collide here where they cannot
 * there.
 */
export function graphSelectionKey(
  selection: NonNullable<GraphSelection>,
): string {
  return `${selection.kind}:${
    selection.kind === 'transition'
      ? transitionEdgeId(selection.stepId, selection.key)
      : selection.id
  }`;
}

/**
 * The step a selection is about, if any node on the canvas should be drawn as
 * selected.
 *
 * A transition or a phase selects no *node*: the arrow or the band is the
 * subject, and lighting a card up as well would say two things are selected
 * when one is.
 */
export function selectedNodeId(selection: GraphSelection): string | undefined {
  return selection?.kind === 'step' ? selection.id : undefined;
}

/**
 * Whether anything is selected — which is what decides whether the panel is
 * open, and therefore whether `Esc` has something to close.
 *
 * Its own name rather than a `!== null` at each site: the check used to be
 * "is a *step* selected", which quietly stopped closing the panel for the two
 * kinds added later while every test still passed.
 */
export function hasGraphSelection(selection: GraphSelection): boolean {
  return selection !== null;
}
